import './index.css'

import React from 'react'

import Button from 'components/ui/button'
import Icon, { IconKey } from 'components/ui/icon'

export type Props = {
  onClick: (...args: Array<any>) => any
  activeMode?: boolean
  compact?: boolean
  size?: string
  icon?: IconKey
  activeIcon?: IconKey
  activeIconColor?: string
  iconColor?: string
  title?: string
  inProgressMode?: boolean
  inProgressIcon?: IconKey
  inProgressColor?: string
  disabled?: boolean
}

/**
 * A button component that can display different states (active, in-progress) with customizable icons
 *
 * @param onClick - Function to call when the button is clicked
 * @param activeMode - Whether the button is in active state
 * @param compact - Whether to use compact styling
 * @param size - Size of the icon (default: '1em')
 * @param icon - Icon to display in normal state
 * @param activeIcon - Icon to display when in active mode
 * @param activeIconColor - Color of the active icon
 * @param iconColor - Color of the normal icon
 * @param title - Tooltip text to display on hover
 * @param inProgressMode - Whether the button is in progress state
 * @param inProgressIcon - Icon to display when in progress mode
 * @param inProgressColor - Color of the progress icon
 * @param disabled - Whether the button is disabled
 * @returns The action button component
 */
const ActionButton = (
  props: Props,
): React.ReactNode | React.ReactElement<'a'> => {
  const {
    activeMode,
    activeIcon,
    activeIconColor,
    compact,
    disabled,
    icon,
    iconColor,
    inProgressMode,
    inProgressIcon,
    inProgressColor,
    onClick,
    size,
    title,
  } = props

  if (inProgressMode && inProgressIcon) {
    return (
      <a className="action-button--animated" title={title}>
        <Icon
          type={inProgressIcon}
          color={inProgressColor}
          width={size}
          height={size}
        />
      </a>
    )
  }

  const renderIcon = activeMode ? activeIcon : icon
  const renderColor = activeMode ? activeIconColor : iconColor
  return (
    <Button
      appearance="icon"
      compact={compact}
      icon={renderIcon}
      disabled={disabled}
      onClick={onClick}
      tooltip={title}
    />
  )
}

ActionButton.defaultProps = {
  size: '1em',
}
export default ActionButton
